import { OauthProvider } from '../components/Constants';

export const LOGIN_ENTRY_POINTS = {
  LOGIN: 'LOGIN',
  CREATE_ACCOUNT_LOGIN: 'CREATE_ACCOUNT_LOGIN',
  FREE_TRIAL_LOGIN: 'FREE_TRIAL_LOGIN',
} as const;

export type LoginEntryPoint =
  (typeof LOGIN_ENTRY_POINTS)[keyof typeof LOGIN_ENTRY_POINTS];

export const NEW_SIGNUP_TYPE = 'NEW_SIGNUP';
export const FREE_TRIAL_SIGNUP_TYPE = 'FREE_TRIAL';

export const isFreeTrialPathname = (pathname: string) => {
  return pathname === '/free-trial' || pathname.startsWith('/free-trial/');
};

export const isCreateAccountPathname = (pathname: string) => {
  return (
    pathname === '/create-account' || pathname.startsWith('/create-account/')
  );
};

export const getLoginEntryPointForPathname = (
  pathname: string
): LoginEntryPoint => {
  if (isFreeTrialPathname(pathname)) {
    return LOGIN_ENTRY_POINTS.FREE_TRIAL_LOGIN;
  }

  if (isCreateAccountPathname(pathname)) {
    return LOGIN_ENTRY_POINTS.CREATE_ACCOUNT_LOGIN;
  }

  return LOGIN_ENTRY_POINTS.LOGIN;
};

export const getSignupTypeForPathname = (pathname: string) => {
  return isFreeTrialPathname(pathname)
    ? FREE_TRIAL_SIGNUP_TYPE
    : NEW_SIGNUP_TYPE;
};

export const getCreateAccountPathForPathname = (pathname: string) => {
  return isFreeTrialPathname(pathname) ? '/free-trial' : '/create-account';
};

export const getCreateAccountEmailPathForPathname = (pathname: string) => {
  return `${getCreateAccountPathForPathname(pathname)}/email`;
};

export const getIntentLoginPathForPathname = (pathname: string) => {
  switch (getLoginEntryPointForPathname(pathname)) {
    case LOGIN_ENTRY_POINTS.CREATE_ACCOUNT_LOGIN:
      return '/create-account/login';
    case LOGIN_ENTRY_POINTS.FREE_TRIAL_LOGIN:
      return '/free-trial/login';
    default:
      return '/login';
  }
};

export const buildOauthQueryParamsForPathname = (pathname: string) => ({
  entry_point: getLoginEntryPointForPathname(pathname),
});

export const buildOauthRedirectUrl = ({
  pathname,
  provider,
  activationKey,
  createAccount,
  encryptedEmail,
}: {
  pathname: string;
  provider: OauthProvider;
  activationKey?: string;
  createAccount?: boolean;
  encryptedEmail?: string;
}) => {
  const params = new URLSearchParams({
    provider,
    ...buildOauthQueryParamsForPathname(pathname),
  });

  if (activationKey) {
    params.set('login_activation_key', activationKey);
  }

  if (createAccount) {
    params.set('create_account', 'true');
  }

  if (encryptedEmail) {
    params.set('email', encryptedEmail);
  }

  return `${RAILS_BASE_URL}/session_transfers/oauth_redirect?${params.toString()}`;
};
